import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAxiosForAuthResponse } from "../Services/commonService";
import {
  API_BETTINGTYPE_OPTIONS,
  API_GET_ALL_MATCH,
  API_GET_FILTER_SPORT,
  API_GET_FILTER_TOURNAMENT,
  API_MARKETTYPE_OPTIONS,
  API_USER_OPTIONS,
} from "../../utils/api/ApiConstant";
import { dateFormat } from "../helper/common";

const useFilteredData = ({
  sport,
  tournament,
  match,
  bettingType,
  role,
  user,
  allMatch = false,
  dependantMatch = true,
  enableTournament = true,
  enableBettingType = false,
  enableSport = true,
  enableRole = true,
  enableUser = true,
  apiParam = "All",
}) => {
  const { data: sportData } = useQuery({
    queryKey: ["filterSport"],
    queryFn: async () =>
      !allMatch
        ? await getAxiosForAuthResponse(API_GET_FILTER_SPORT, {
            isActive: true,
          })
        : [],
    enabled: !allMatch && enableSport,
  });

  const { data: tournamentData } = useQuery({
    queryKey: ["filterTournament", sport],
    queryFn: async () =>
      sport
        ? await getAxiosForAuthResponse(API_GET_FILTER_TOURNAMENT, {
            isActive: true,
            eventTypeId: sport,
          })
        : [],
    enabled: !!sport && enableTournament,
  });

  const { data: matchData } = useQuery({
    queryKey: ["filterMatch", tournament],
    queryFn: async () =>
      tournament
        ? await getAxiosForAuthResponse(API_GET_ALL_MATCH, {
            isActive: true,
            competitionId: tournament,
          })
        : [],
    enabled: !!tournament && dependantMatch,
  });

  const { data: matchAllData } = useQuery({
    queryKey: ["filterMatchAll"],
    queryFn: async () =>
      allMatch
        ? await getAxiosForAuthResponse(API_GET_ALL_MATCH, { isActive: true })
        : [],
    enabled: allMatch,
  });

  const { data: bettingTypeData } = useQuery({
    queryKey: ["filterBettingType", sport],
    queryFn: async () =>
      sport
        ? await getAxiosForAuthResponse(API_BETTINGTYPE_OPTIONS, {
            isActive: true,
            eventTypeId: sport,
          })
        : [],
    enabled: !!sport && enableBettingType,
  });

  const { data: marketTypeData } = useQuery({
    queryKey: ["filterMarketType", bettingType],
    queryFn: async () =>
      bettingType
        ? await getAxiosForAuthResponse(API_MARKETTYPE_OPTIONS, {
            isActive: true,
            bettingTypeId: bettingType,
          })
        : [],
    enabled: !!bettingType,
  });

  const { data: roleData } = useQuery({
    queryKey: ["filterRole"],
    queryFn: async () =>
      enableRole ? await getAxiosForAuthResponse(API_ROLES) : [],
    enabled: enableRole,
  });

  const { data: userData } = useQuery({
    queryKey: ["filterUser"],
    queryFn: async () =>
      enableUser
        ? await getAxiosForAuthResponse(`${API_USER_OPTIONS}?self=false`)
        : [],
    enabled: enableUser,
  });

  const sportOptions = useMemo(
    () =>
      (Array.isArray(sportData) ? sportData : []).map((item) => ({
        value: item.Id,
        label: item.Name,
      })),
    [sportData],
  );

  const roleOptions = useMemo(
    () =>
      (Array.isArray(roleData) ? roleData : []).map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [roleData],
  );

  const userOptions = useMemo(
    () =>
      (Array.isArray(userData) ? userData : []).map((item) => ({
        value: item.value,
        label: item.label,
      })),
    [userData],
  );

  const tournamentOptions = useMemo(
    () =>
      (Array.isArray(tournamentData) ? tournamentData : []).map((item) => ({
        value: item.Id,
        label: item.Name,
      })),
    [tournamentData],
  );

  const matchOptions = useMemo(
    () =>
      (Array.isArray(matchData) ? matchData : []).map((item) => ({
        value: item.Id,
        label: item.Name,
      })),
    [matchData],
  );

  const matchAllOptions = useMemo(
    () =>
      (Array.isArray(matchAllData) ? matchAllData : []).map((item) => ({
        value: item.Id,
        label: item.Name,
      })),
    [matchAllData],
  );

  const bettingTypeOptions = useMemo(
    () =>
      (Array.isArray(bettingTypeData) ? bettingTypeData : []).map((item) => ({
        value: item.Id,
        label: item.Name,
      })),
    [bettingTypeData],
  );

  const marketTypeOptions = useMemo(
    () =>
      (Array.isArray(marketTypeData) ? marketTypeData : []).map((item) => ({
        value: item.Id,
        label: item.Name,
      })),
    [marketTypeData],
  );

  return {
    sportOptions,
    roleOptions,
    userOptions,
    tournamentOptions,
    matchOptions,
    matchAllOptions,
    bettingTypeOptions,
    marketTypeOptions,
  };
};

export default useFilteredData;
